import React, { useEffect } from 'react';
import { Portal } from '../../../../components/Portal/Portal';
import { useTier } from '../../../../contexts/TierContext';
import { useTranslation } from 'react-i18next';
import accomplishedIcon from '../../../../assets/icons/Tier-icons-accomplished.svg';
import nextUpIcon from '../../../../assets/icons/Tier-icons-next-up.svg';
import upComingIcon from '../../../../assets/icons/Tier-icons-up-coming.svg';
import closeIcon from '../../../../assets/icons/close-icon.svg';
import '../../../../styles/_utilities.scss';
import './TierProgressModal.scss';

interface TierProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  userPoints: {
    projectValue: {
      current: number;
      total: number;
    };
    engagement: {
      current: number;
      total: number;
    };
  };
}

interface TierInfo {
  name: string;
  specPointsRange: [number, number];
  engagementPoints: number;
}

const tiers: TierInfo[] = [
  {
    name: 'bronze',
    specPointsRange: [0, 50000],
    engagementPoints: 0,
  },
  {
    name: 'silver',
    specPointsRange: [50000, 125000],
    engagementPoints: 20,
  },
  {
    name: 'gold',
    specPointsRange: [125000, 250000],
    engagementPoints: 30,
  },
  {
    name: 'platinum',
    specPointsRange: [250000, 500000],
    engagementPoints: 40,
  },
  {
    name: 'accolade',
    specPointsRange: [500000, Infinity],
    engagementPoints: 50,
  },
];

export const TierProgressModal: React.FC<TierProgressModalProps> = ({ isOpen, onClose, userPoints }) => {
  const { tier } = useTier();
  const { t } = useTranslation();

  // Handle body scroll locking
  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  const getTierStatus = (tierName: string) => {
    const currentTierIndex = tiers.findIndex(t => t.name === tier);
    const thisTierIndex = tiers.findIndex(t => t.name === tierName);

    if (thisTierIndex < currentTierIndex) {
      return { icon: accomplishedIcon, status: 'completed' };
    } else if (thisTierIndex === currentTierIndex) {
      return { icon: accomplishedIcon, status: 'current' };
    } else if (thisTierIndex === currentTierIndex + 1) {
      return { icon: nextUpIcon, status: 'next' };
    } else {
      return { icon: upComingIcon, status: 'upcoming' };
    }
  };

  if (!isOpen) return null;

  return isOpen ? (
    <Portal>
      <div className="tier-progress-overlay">
        <div className="tier-progress-modal">
          <button
            className="tier-progress__close"
            onClick={onClose}
            aria-label={t('home:tierProgress.closeModal')}
          >
            <img src={closeIcon} alt="" />
          </button>
          <h2 className="tier-progress__title">{t('home:tierProgress.title')}</h2>

          <div className="tier-progress__timeline">
            {tiers.map((tierInfo) => {
              const { icon, status } = getTierStatus(tierInfo.name);
              return (
                <div
                  key={tierInfo.name}
                  className={`tier-progress__item tier-progress__item--${status}`}
                >
                  <div className={`tier-progress__pill tier-progress__pill--${tierInfo.name} ${status === 'current' ? 'tier-progress__pill--current' : ''}`}>
                    <img src={icon} alt="" className={`tier-progress__icon tier-progress__icon--${tierInfo.name}`} />
                    <span className="tier-progress__tier-name">{t(`home:dashboard.tiers.${tierInfo.name}`)}</span>
                  </div>

                  <div className="tier-progress__points">
                    {tierInfo.name === 'bronze' ? (
                      <div className="tier-progress__points-group">
                        <span className="tier-progress__spec-points">At Signup</span>
                      </div>
                    ) : (
                      <>
                        <div className="tier-progress__points-group">
                          <span className="tier-progress__spec-points">
                            {status === 'current'
                              ? `${userPoints.projectValue.current.toLocaleString()}/${tierInfo.specPointsRange[1].toLocaleString()}`
                              : tierInfo.specPointsRange[1] === Infinity
                                ? `${tierInfo.specPointsRange[0].toLocaleString()} +`
                                : tierInfo.specPointsRange[0].toLocaleString()
                            }
                          </span>
                          <span className="tier-progress__points-label">{t('home:tierProgress.specificationPoints')}</span>
                        </div>

                        <span className="tier-progress__and">&</span>
                        <div className="tier-progress__points-group">
                          <span className="tier-progress__engagement-points">
                            {tierInfo.engagementPoints}
                          </span>
                          <span className="tier-progress__points-label">{t('home:tierProgress.engagementPoints')}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Portal>
  ) : null;
};
